import axios from "axios";

const TOKEN_API_URL =
  process.env.REACT_APP_TOKEN_API_URL || "https://token-proxy-asd3etazf5a3h7fr.canadacentral-01.azurewebsites.net/api/token";
const TOKEN_REFRESH_BUFFER = 5 * 60 * 1000; // Refresh 5 minutes before expiry

class TokenService {
  constructor() {
    this.tokenData = null;
    this.tokenPromise = null;
    this.refreshTimer = null;
  }

  /**
   * Get access token from local API
   * Returns cached token if valid, otherwise fetches new one
   */
  async getAccessToken() {
    // Return cached token if still valid
    if (this.tokenData && this.isTokenValid()) {
      return this.tokenData.access_token;
    }

    // If a token fetch is already in progress, wait for it
    if (this.tokenPromise) {
      const tokenData = await this.tokenPromise;
      return tokenData.access_token;
    }

    // Fetch new token
    this.tokenPromise = this.fetchNewToken();
    try {
      this.tokenData = await this.tokenPromise;
      this.scheduleTokenRefresh();
      return this.tokenData.access_token;
    } finally {
      this.tokenPromise = null;
    }
  }

  /**
   * Fetch new token from API
   */
  async fetchNewToken() {
    try {
      console.log(
        "%c🔐 Fetching new access token...",
        "color: #f59e0b; font-weight: bold"
      );
      console.log("Token API URL:", TOKEN_API_URL);

      const response = await axios.get(TOKEN_API_URL);
      const tokenData = response.data;

      // Store token data in sessionStorage
      sessionStorage.setItem("tokenData", JSON.stringify(tokenData));
      sessionStorage.setItem("accessToken", tokenData.access_token);

      const expiresAt = new Date(tokenData.expires_at);
      const expiresIn = Math.round((expiresAt - Date.now()) / 1000 / 60);

      console.log(
        "%c✅ Token fetched successfully!",
        "color: #10b981; font-weight: bold"
      );
      console.log("Token Type:", tokenData.token_type);
      console.log("Expires At:", expiresAt.toLocaleString());
      console.log("Valid For:", expiresIn, "minutes");

      return tokenData;
    } catch (error) {
      console.error(
        "%c❌ Error fetching access token",
        "color: #ef4444; font-weight: bold",
        error
      );
      console.error("Token API URL:", TOKEN_API_URL);
      console.error("Error details:", error.message);
      throw new Error("Failed to fetch access token");
    }
  }

  /**
   * Check if current token is still valid
   */
  isTokenValid() {
    if (!this.tokenData || !this.tokenData.expires_at) {
      return false;
    }

    const expiresAt = new Date(this.tokenData.expires_at).getTime();
    const now = Date.now();

    // Token is valid if it hasn't expired yet (with buffer)
    return expiresAt - now > TOKEN_REFRESH_BUFFER;
  }

  /**
   * Schedule automatic token refresh before expiry
   */
  scheduleTokenRefresh() {
    // Clear existing timer
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    if (!this.tokenData || !this.tokenData.expires_at) {
      return;
    }

    const expiresAt = new Date(this.tokenData.expires_at).getTime();
    const now = Date.now();
    const refreshIn = expiresAt - now - TOKEN_REFRESH_BUFFER;

    if (refreshIn > 0) {
      const refreshInMinutes = Math.round(refreshIn / 1000 / 60);
      console.log(
        `%c⏰ Token auto-refresh scheduled in ${refreshInMinutes} minutes`,
        "color: #8b5cf6; font-weight: bold"
      );
      this.refreshTimer = setTimeout(async () => {
        console.log(
          "%c🔄 Auto-refreshing token...",
          "color: #f59e0b; font-weight: bold"
        );
        try {
          await this.getAccessToken();
        } catch (error) {
          console.error(
            "%c❌ Failed to auto-refresh token",
            "color: #ef4444; font-weight: bold",
            error
          );
        }
      }, refreshIn);
    }
  }

  /**
   * Initialize token service (load from sessionStorage if available)
   */
  initialize() {
    console.log(
      "%c🔧 Initializing Token Service...",
      "color: #3b82f6; font-weight: bold"
    );
    const storedTokenData = sessionStorage.getItem("tokenData");
    if (storedTokenData) {
      try {
        this.tokenData = JSON.parse(storedTokenData);
        if (this.isTokenValid()) {
          this.scheduleTokenRefresh();
          const expiresAt = new Date(this.tokenData.expires_at);
          console.log(
            "%c✅ Token loaded from sessionStorage",
            "color: #10b981; font-weight: bold"
          );
          console.log("Token expires at:", expiresAt.toLocaleString());
        } else {
          console.log(
            "%c⚠️ Stored token expired, will fetch new one",
            "color: #f59e0b; font-weight: bold"
          );
          this.tokenData = null;
        }
      } catch (error) {
        console.error(
          "%c❌ Error loading token from sessionStorage",
          "color: #ef4444; font-weight: bold",
          error
        );
      }
    } else {
      console.log(
        "%cℹ️ No cached token found, will fetch new one",
        "color: #6b7280; font-weight: bold"
      );
    }
  }

  /**
   * Clear token data and cancel refresh timer
   */
  clearToken() {
    this.tokenData = null;
    sessionStorage.removeItem("tokenData");
    sessionStorage.removeItem("accessToken");
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }
}

// Export singleton instance
const tokenService = new TokenService();
tokenService.initialize();

export default tokenService;
